import { DockviewApi, DockviewPanelApi } from "dockview-react";
import React, { useEffect } from "react";
import SafeNumberInput from "./SafeNumberInput";
import { BlackScholes, Option } from "@uqee/black-scholes";
import { api } from "./utils/api";

interface ModelRanges {
  uLeft: number;
  uRight: number;
  uStep: number;

  sLeft: number;
  sRight: number;
  sStep: number;

  volLeft: number;
  volRight: number;
  volStep: number;

  rLeft: number;
  rRight: number;
  rStep: number;

  tLeft: number;
  tRight: number;
  tStep: number;
}

interface OptUserInputs {
  u: number;
  s: number;
  vol: number;
  r: number;
  t: number;
}

interface ModelPrices {
  call: number;
  put: number;
}

const OptPricingModel = (props: {
  api: DockviewPanelApi;
  containerApi: DockviewApi;
}) => {
  const [modelRanges, setModelRanges] = React.useState<ModelRanges>({
    uLeft: 50,
    uRight: 150,
    uStep: 0.25,

    sLeft: 1,
    sRight: 200,
    sStep: 0.25,

    volLeft: 0.2,
    volRight: 3,
    volStep: 0.05,

    rLeft: 0.01,
    rRight: 0.08,
    rStep: 0.001,

    tLeft: 1,
    tRight: 100,
    tStep: 1,
  });

  const [userInputs, setUserInputs] = React.useState<OptUserInputs>({
    u: 100,
    s: 100,
    vol: 0.8,
    r: 0.045,
    t: 50.0,
  });

  const [prices, setPrices] = React.useState<ModelPrices>({
    call: 0,
    put: 0,
  });

  const [autoContract, setAutoContract] = React.useState<string>("");

  useEffect(() => {
    const blackScholes: BlackScholes = new BlackScholes();

    const call: Option = blackScholes.option({
      rate: userInputs.r,
      sigma: userInputs.vol,
      strike: userInputs.s,
      time: userInputs.t / 365,
      type: "call",
      underlying: userInputs.u,
    });

    const put: Option = blackScholes.option({
      rate: userInputs.r,
      sigma: userInputs.vol,
      strike: userInputs.s,
      time: userInputs.t / 365,
      type: "put",
      underlying: userInputs.u,
    });

    setPrices({
      call: call.price,
      put: put.price,
    });
  }, [userInputs]);

  return (
    <div className="h-full w-full m-4 flex flex-col gap-4">
      {/* AUTOMATIC INPUTS */}
      <form
      className="w-[400px]"
        onSubmit={(e) => {
          e.preventDefault();
          if (!autoContract) return;
          api
            .get(
              `/underlying-expiration?ticker=${encodeURIComponent(
                autoContract
              )}`
            )
            .then((res) => {
              const data = res.data;

              setModelRanges((prev) => ({
                ...prev,
                uLeft: data.strike_price * 0.9,
                uRight: data.strike_price * 1.1,
                sLeft: data.strike_price * 0.9,
                sRight: data.strike_price * 1.1,
                tLeft: 1,
                tRight: data.days_remaining * 2,
              }));

              setUserInputs((prev) => ({
                ...prev,
                u: data.strike_price,
                s: data.strike_price,
                t: data.days_remaining,
              }));
            })
            .catch((e) => console.error(e));
        }}
      >
        <label htmlFor="contract">Contract:</label>
        <input
          className=""
          id="contract"
          type="text"
          value={autoContract}
          onChange={(e) => setAutoContract(e.target.value)}
        />

        <button type="submit" className="hidden">Submit</button>
      </form>
      {/* UNDERLYING */}
      <div className="flex flex-row gap-4 items-start">
        <div className="w-20">
          <label htmlFor="u_left">U min:</label>
          <SafeNumberInput
            id="u_left"
            value={modelRanges.uLeft}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                uLeft: v,
              });
            }}
            min={0}
            max={modelRanges.uRight}
          />
        </div>

        <div className="w-52">
          <label htmlFor="underlying_price">Underlying:</label>
          <input
            className="w-full"
            id="underlying_price"
            type="range"
            min={modelRanges.uLeft}
            max={modelRanges.uRight}
            step={modelRanges.uStep}
            value={userInputs.u}
            onChange={(e) => {
              setUserInputs({ ...userInputs, u: parseFloat(e.target.value) });
            }}
          />
          <div>{userInputs.u.toFixed(2)}</div>
        </div>

        <div className="w-20">
          <label htmlFor="u_right">U max:</label>
          <SafeNumberInput
            id="u_right"
            value={modelRanges.uRight}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                uRight: v,
              });
            }}
          />
        </div>

        <div className="w-20">
          <label htmlFor="u_step">U step:</label>
          <SafeNumberInput
            id="u_step"
            value={modelRanges.uStep}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                uStep: v,
              });
            }}
            min={0.01}
          />
        </div>
      </div>

      {/* STRIKE */}
      <div className="flex flex-row gap-4 items-start">
        <div className="w-20">
          <label htmlFor="s_left">S min:</label>
          <SafeNumberInput
            id="s_left"
            value={modelRanges.sLeft}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                sLeft: v,
              });
            }}
            min={0}
            max={modelRanges.sRight}
          />
        </div>

        <div className="w-52">
          <label htmlFor="strike_price">Strike:</label>
          <input
            className="w-full"
            id="strike_price"
            type="range"
            min={modelRanges.sLeft}
            max={modelRanges.sRight}
            step={modelRanges.sStep}
            value={userInputs.s}
            onChange={(e) => {
              setUserInputs({ ...userInputs, s: parseFloat(e.target.value) });
            }}
          />
          <div>{userInputs.s.toFixed(2)}</div>
        </div>

        <div className="w-20">
          <label htmlFor="s_right">S max:</label>
          <SafeNumberInput
            id="s_right"
            value={modelRanges.sRight}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                sRight: v,
              });
            }}
          />
        </div>

        <div className="w-20">
          <label htmlFor="s_step">S step:</label>
          <SafeNumberInput
            id="s_step"
            value={modelRanges.sStep}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                sStep: v,
              });
            }}
            min={0.01}
          />
        </div>
      </div>

      {/* VOLATILITY */}
      <div className="flex flex-row gap-4 justify-start">
        <div className="w-20">
          <label htmlFor="v_left">V min:</label>
          <SafeNumberInput
            id="v_left"
            value={modelRanges.volLeft}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                volLeft: v,
              });
            }}
            min={0}
            max={modelRanges.volRight}
          />
        </div>

        <div className="w-52">
          <label htmlFor="volatility">Volatility:</label>
          <input
            className="w-full"
            id="volatility"
            type="range"
            min={modelRanges.volLeft}
            max={modelRanges.volRight}
            step={modelRanges.volStep}
            value={userInputs.vol}
            onChange={(e) => {
              setUserInputs({ ...userInputs, vol: parseFloat(e.target.value) });
            }}
          />
          <div>{userInputs.vol.toFixed(3)}</div>
        </div>

        <div className="w-20">
          <label htmlFor="v_right">V max:</label>
          <SafeNumberInput
            id="v_right"
            value={modelRanges.volRight}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                volRight: v,
              });
            }}
          />
        </div>

        <div className="w-20">
          <label htmlFor="v_step">V step:</label>
          <SafeNumberInput
            id="v_step"
            value={modelRanges.volStep}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                volStep: v,
              });
            }}
            min={0.01}
          />
        </div>
      </div>

      {/* RATE */}
      <div className="flex flex-row gap-4 justify-start">
        <div className="w-20">
          <label htmlFor="r_left">R min:</label>
          <SafeNumberInput
            id="r_left"
            value={modelRanges.rLeft}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                rLeft: v,
              });
            }}
            min={0}
            max={modelRanges.rRight}
          />
        </div>

        <div className="w-52">
          <label htmlFor="risk_free_rate">Risk-free rate:</label>
          <input
            className="w-full"
            id="risk_free_rate"
            type="range"
            min={modelRanges.rLeft}
            max={modelRanges.rRight}
            step={modelRanges.rStep}
            value={userInputs.r}
            onChange={(e) => {
              setUserInputs({ ...userInputs, r: parseFloat(e.target.value) });
            }}
          />
          <div>{userInputs.r.toFixed(2)}</div>
        </div>

        <div className="w-20">
          <label htmlFor="r_right">R max:</label>
          <SafeNumberInput
            id="r_right"
            value={modelRanges.rRight}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                rRight: v,
              });
            }}
          />
        </div>

        <div className="w-20">
          <label htmlFor="r_step">R step:</label>
          <SafeNumberInput
            id="r_step"
            value={modelRanges.rStep}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                rStep: v,
              });
            }}
            min={0.001}
          />
        </div>
      </div>

      {/* TIME */}
      <div className="flex flex-row gap-4 justify-start">
        <div className="w-20">
          <label htmlFor="t_left">T min:</label>
          <SafeNumberInput
            id="t_left"
            value={modelRanges.tLeft}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                tLeft: v,
              });
            }}
            min={0}
            max={modelRanges.tRight}
          />
        </div>

        <div className="w-52">
          <label htmlFor="time_to_expiration">Time to expiration:</label>
          <input
            className="w-full"
            id="time_to_expiration"
            type="range"
            min={modelRanges.tLeft}
            max={modelRanges.tRight}
            step={modelRanges.tStep}
            value={userInputs.t}
            onChange={(e) => {
              setUserInputs({ ...userInputs, t: parseFloat(e.target.value) });
            }}
          />
          <div>{userInputs.t.toFixed(2)}</div>
        </div>

        <div className="w-20">
          <label htmlFor="t_right">T max:</label>
          <SafeNumberInput
            id="t_right"
            value={modelRanges.tRight}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                tRight: v,
              });
            }}
          />
        </div>

        <div className="w-20">
          <label htmlFor="t_step">T step:</label>
          <SafeNumberInput
            id="t_step"
            value={modelRanges.tStep}
            onChange={(v) => {
              setModelRanges({
                ...modelRanges,
                tStep: v,
              });
            }}
            min={0.1}
          />
        </div>

        <p></p>
      </div>

      <div>
        <p className="mt-4">Results:</p>
        <p>&gt; C: {prices.call.toFixed(3)}</p>
        <p>&gt; P: {prices.put.toFixed(3)}</p>
      </div>
    </div>
  );
};

export default OptPricingModel;
